
/** vendor */
import React, { useEffect, useState, useRef } from 'react'
import { useSelector } from 'react-redux'
import { SearchIcon } from '@heroicons/react/solid'
import imageCompression from 'browser-image-compression'
import { validateRecipe } from '../../../services/user.service'

export default function CreateBasicDetailsForm({onUpdate, recipe}) {
    const recipeOptions = useSelector(state => state.form_options?.recipe_options)
    const [loading, setLoading] = useState(false)
    const [nameAvailable, setNameAvailable] = useState(false)
    const [nameTakenWarning, setNameTakenWarning] = useState(false)
    const [imagePreview, setImagePreview] = useState(recipe?.image ?? '')

    const [imageError, setImageError] = useState('')
    const [imageProcessing, setImageProcessing] = useState(false)
    const [imageSuccess, setImageSuccess] = useState('')

    const [formFragment, setFormFragment] = useState({
        name: '',
        description: '',
        image: '',
        serves: 1
    })

    let debounceTimeout = useRef(null)

    const cropAndCenterImage = (file) => {
        return new Promise((resolve, reject) => {
          if (!file || !file.type.startsWith('image/')) {
            return reject(new Error('Only image files are allowed.'));
          }
      
          const img = new Image();
          const reader = new FileReader();
      
          reader.onload = (e) => {
            img.onload = () => {
              const targetWidth = 1920;
              const targetHeight = 1080;
      
              const aspectRatio = img.width / img.height;
              const targetRatio = targetWidth / targetHeight;
      
              let srcWidth, srcHeight, srcX, srcY;
      
              if (aspectRatio > targetRatio) {
                // Image is wider than target aspect ratio
                srcHeight = img.height;
                srcWidth = targetRatio * img.height;
                srcX = (img.width - srcWidth) / 2;
                srcY = 0;
              } else {
                // Image is taller than target aspect ratio
                srcWidth = img.width;
                srcHeight = img.width / targetRatio;
                srcX = 0;
                srcY = (img.height - srcHeight) / 2;
              }
      
              const canvas = document.createElement('canvas');
              canvas.width = targetWidth;
              canvas.height = targetHeight;
      
              const ctx = canvas.getContext('2d');
              ctx.drawImage(img, srcX, srcY, srcWidth, srcHeight, 0, 0, targetWidth, targetHeight);
      
              canvas.toBlob(
                (blob) => {
                  if (!blob) {
                    return reject(new Error('Image processing failed.'));
                  }
      
                  if (blob.size > 2 * 1024 * 1024) {
                    return reject(new Error('Processed image exceeds 2MB.'));
                  }
      
                  const finalFile = new File([blob], 'resized.jpg', { type: 'image/jpeg' });
                  resolve(finalFile);
                },
                'image/jpeg',
                0.9
              );
            };
      
            img.onerror = () => reject(new Error('Failed to load image.'));
            img.src = e.target.result;
          };
      
          reader.onerror = () => reject(new Error('Failed to read file.'));
          reader.readAsDataURL(file);
        });
      };

    const processImage = async (file) => {
        const options = {
            maxWidthOrHeight: 1920, 
            useWebWorker: true,
            maxSizeMB: 2,
            fileType: 'image/jpeg',
        }
    
        const compressedFile = await imageCompression(file, options)
        return compressedFile
    }

    /**
     * Hooks
     */
    useEffect(() => {
        if (recipe?.name) {
            setFormFragment({
                name: recipe?.name ?? '',
                description: recipe?.description ?? '',
                image: recipe?.image ?? '',
                serves: recipe?.serves ?? 1
            })
        }
    }, [recipe])

    /**
     * Handlers
     */
    const handleOnChangeDescription = async e => {
        e.preventDefault()
        const formFragmentCopy = {...formFragment}
        formFragmentCopy[e.target.id] = e.target.value
        
        setFormFragment(formFragmentCopy)
        onUpdate(formFragmentCopy)
    }


    const handleOnChangeName = async e => {
        e.preventDefault()
        const formFragmentCopy = {...formFragment}
        formFragmentCopy[e.target.id] = e.target.value

        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current)
        }

        debounceTimeout.current = setTimeout(async () => {
            const results = await validateRecipe({...recipe, ...{name: e.target.value}})
            setNameTakenWarning(!!results?.errors?.name)
        }, 500)

        setFormFragment(formFragmentCopy)
        onUpdate(formFragmentCopy)
    }

    const handleOnRemoveImage = () => {
        setFormFragment({ ...formFragment, image: '' })
    }

    const handleImageUpload = async (event) => {
        const file = event.target.files[0]
        if (!file) return
      
        if (!file.type.startsWith('image/')) {
            setImageError('only image files are allowed.')
            return;
        }
      
        if (file.size > 2 * 1024 * 1024) {
            setImageError('file size must be under 2MB.')
            return
        }

        setImageError('')
        setImageProcessing(true)
      
        try {
            const compressedFile = await processImage(file)
            const croppedAndCenteredImage = await cropAndCenterImage(file)
            const previewURL = URL.createObjectURL(croppedAndCenteredImage)
            
            setImagePreview(previewURL)
            setFormFragment({ ...formFragment, image: compressedFile })
            setImageProcessing(false)
            setImageSuccess(true)
        } catch (error) {
            setImageError('failed to process image.')
            setFormFragment({ ...formFragment, image: '' })

            const previewURL = 
                recipe?.image ? URL.createObjectURL(recipe?.image) : ''

            setImagePreview(previewURL)
            setImageProcessing(false)
            setImageSuccess(false)
        }
    }

    return (
        <div className="min-w-full h-[60vh] pt-4">
            <div className="flex min-w-full justify-center"> 
                <div className="w-full mr-2 px-8">
                        <div>
                            <label htmlFor="name" className="block mb-1 text-xs text-an_green-dark">recipe name</label>
                            <div className="relative">
                                <input 
                                    type="text" 
                                    id="name"
                                    value={formFragment?.name} 
                                    className="bg-an_white border border-an_green-dark text-an_green-dark text-sm rounded-lg block w-full pr-10 p-2.5" 
                                    placeholder="recipe name ..."
                                    onChange={handleOnChangeName}
                                />
                                <div className="flex absolute inset-y-0 right-0 items-center pr-3 pointer-events-none">
                                    <SearchIcon className="h-6 w-6" />
                                </div>
                            </div>
                        </div>
                        <div className="mb-2 text-red-600 mt-1 text-xs h-3 leading-none">{ nameTakenWarning && 'This name is already taken.' }</div>

                        <div className="mb-2">
                            <label htmlFor="description" className="block mb-1 text-xs text-an_green-dark">description</label>
                            <textarea
                                value={formFragment?.description} 
                                onChange={handleOnChangeDescription}
                                id="description" 
                                rows="4" 
                                className="placeholder-an_green block p-2.5 w-full text-sm text-an_green-dark rounded-lg border border-an_green-dark bg-an_white" 
                                placeholder="description ..."></textarea>
                        </div>

                        <div className="mb-2">
                            <div className="flex justify-between w-full mb-2">
                                <label className="block text-an_green-dark text-xs" htmlFor="user_avatar">image</label>
                                { 
                                    formFragment?.image && 
                                        <button
                                            onClick={() => handleOnRemoveImage()} 
                                            className="block text-an_green-dark text-xs underline"
                                        >remove</button> 
                                }
                            </div>

                            {
                                !formFragment?.image && 
                                    <div className="w-full relative rounded-lg h-36 overflow-hidden border-an_green-dark border">
                                        <input 
                                            className="block opacity-0 h-36 w-full overflow-hidden text-sm text-an_green-dark rounded-lg border border-black cursor-pointer" 
                                            aria-describedby="image_help" 
                                            id="image" 
                                            name="image"
                                            type="file"
                                            accept="image/*"
                                            // value={formFragment?.image}  
                                            onChange={handleImageUpload}
                                        />
                                        <label
                                            htmlFor="image" 
                                            className="mt-1 absolute block text-center w-full top-12 an_green-dark text-xs" 
                                            id="image_help"
                                        >(optional) upload a picture of<br/>the meal ready to serve</label>
                                    </div>
                            }

                            {
                                formFragment?.image &&
                                    <div className="w-full border-an_green-dark border rounded-lg">
                                        <img src={imagePreview} className="max-w-full max-h-full mx-auto" />
                                    </div>
                            }
                        </div>
                </div>
            </div>
        </div>
    )
}

